import { parsePitch, midiOf, fingerChord, type Duration } from '@simplenotation/theory';
import type { Beat, Note, Score } from './types';
import { createScore, createNote, createBeat, createRest } from './factories';

const Q: Duration = { base: 4, dots: 0 };

/**
 * Acceptance-test fixture (§7): one guitar track, standard tuning, treble-8vb
 * staff + tab, 4/4, C major, ♩=120. Bar 1 = A4 G4 A4 + quarter rest.
 */
export function fixtureScore(): Score {
  const s = createScore({ title: 'Untitled', bars: 1, instrument: 'classical-guitar' });
  const beats = ['A4', 'G4', 'A4'].map((p) => createBeat(Q, [createNote(parsePitch(p))]));
  beats.push(createRest(Q));
  s.tracks[0].bars[0].voices[0] = beats;
  fingerAll(s);
  return s;
}

/** Give every note of a fretted track a tab position (simple, context-free). */
export function fingerAll(s: Score): Score {
  for (const t of s.tracks) {
    if (!t.tuning) continue;
    for (const tb of t.bars) {
      for (const v of tb.voices) {
        for (const b of v) {
          if (b.rest || !b.notes.length || b.notes.every((n) => n.string !== undefined)) continue;
          const res = fingerChord(
            b.notes.map((n) => midiOf(n.pitch) + t.writtenToSounding),
            { tuning: t.tuning, capo: t.capo ?? 0 },
          );
          b.notes.forEach((n, i) => {
            const p = res?.[i];
            if (p) {
              n.string = p.string;
              n.fret = p.fret;
            }
          });
        }
      }
    }
  }
  return s;
}

/** A short demo piece shown to first-time visitors. */
export function demoScore(): Score {
  const s = createScore({ title: 'Untitled', bars: 4, instrument: 'classical-guitar' });
  const E: Duration = { base: 8, dots: 0 };
  const H: Duration = { base: 2, dots: 0 };
  const bar = (spec: Array<[Duration, string[]]>) =>
    spec.map(([d, ps]) => (ps.length ? createBeat(d, ps.map((p) => createNote(parsePitch(p)))) : createRest(d)));
  const t = s.tracks[0];
  t.bars[0].voices[0] = bar([
    [Q, ['A4']],
    [Q, ['G4']],
    [Q, ['A4']],
    [Q, ['C5']],
  ]);
  t.bars[1].voices[0] = bar([
    [Q, ['E5']],
    [Q, ['D5']],
    [Q, ['G5', 'B5']],
    [E, ['C6']],
    [E, ['Bb5']],
  ]);
  t.bars[2].voices[0] = bar([
    [E, ['A5']],
    [E, ['G5']],
    [E, ['F5']],
    [E, ['E5']],
    [Q, ['D5']],
    [Q, ['C5', 'E5']],
  ]);
  t.bars[3].voices[0] = bar([
    [H, ['C5', 'E5', 'G5']],
    [Q, []],
    [Q, []],
  ]);
  return fingerAll(s);
}

// ---------------------------------------------------------------- showcase

type Pos = [string: number, fret: number];
const pos = (p: string, at: Pos, extra: Partial<Note> = {}): Note => createNote(parsePitch(p), { string: at[0], fret: at[1], ...extra });
const dur = (base: Duration['base'], dots: 0 | 1 = 0, triplet = false): Duration => (triplet ? { base, dots, tuplet: { actual: 3, normal: 2 } } : { base, dots });
const hit = (d: Duration, notes: Note[], extra: Partial<Beat> = {}): Beat => ({ ...createBeat(d, notes), ...extra });

/** Open-position chord voicings, written pitch (guitar sounds an octave lower), string 6 → 1. */
const EM = (): Note[] => [pos('E3', [6, 0]), pos('B3', [5, 2]), pos('E4', [4, 2]), pos('G4', [3, 0]), pos('B4', [2, 0]), pos('E5', [1, 0])];
const CMAJ = (): Note[] => [pos('C4', [5, 3]), pos('E4', [4, 2]), pos('G4', [3, 0]), pos('C5', [2, 1]), pos('E5', [1, 0])];
const GMAJ = (): Note[] => [pos('G3', [6, 3]), pos('B3', [5, 2]), pos('D4', [4, 0]), pos('G4', [3, 0]), pos('B4', [2, 0]), pos('G5', [1, 3])];
const B7 = (): Note[] => [pos('B3', [5, 2]), pos('D#4', [4, 1]), pos('A4', [3, 2]), pos('B4', [2, 0]), pos('F#5', [1, 2])];

/** A down-down-up-up-down-up strumming bar on one chord; the first two hits are palm-muted. */
function strumBar(chord: () => Note[], name: string, extra: Partial<Beat> = {}): Beat[] {
  const E8 = dur(8);
  return [
    hit(dur(4), chord(), { strum: 'down', palmMute: true, chord: { name }, ...extra }),
    hit(E8, chord(), { strum: 'down', palmMute: true }),
    hit(E8, chord(), { strum: 'up' }),
    hit(E8, chord(), { strum: 'up' }),
    hit(E8, chord(), { strum: 'down' }),
    hit(dur(4), chord(), { strum: 'up' }),
  ];
}

/**
 * The piece shown to first-time visitors (`/new?demo=1`): twelve bars in
 * E minor that touch most of what the editor can show. A: fingerpicked
 * arpeggios with let-ring; B: a melody with hammer-on, slide, vibrato, a
 * triplet, a bend and a crescendo; C: strummed chords with palm mute,
 * strum directions, a repeat with first and second endings and a fermata.
 * Stays in 4/4 at ♩=120 throughout.
 */
export function showcaseScore(): Score {
  const s = createScore({ title: 'Riverside Étude', bars: 12, fifths: 1, bpm: 120, instrument: 'classical-guitar' });
  s.meta.subtitle = 'Study in E minor';
  s.meta.composer = 'SimpleNotation';
  s.bars[0].keySig = { fifths: 1, mode: 'minor' };
  s.bars[0].section = { letter: 'A', name: 'Intro' };
  s.bars[4].section = { letter: 'B', name: 'Melody' };
  s.bars[8].section = { letter: 'C', name: 'Strum' };
  s.bars[8].repeatStart = true;
  s.bars[10].repeatEnd = 2;
  s.bars[10].endings = [1];
  s.bars[11].endings = [2];

  const E8 = dur(8);
  const Q = dur(4);
  const H = dur(2);
  const T = dur(8, 0, true);
  const ring = (d: Duration, n: Note, extra: Partial<Beat> = {}): Beat => hit(d, [n], { letRing: true, ...extra });
  const b = s.tracks[0].bars;

  // A: arpeggios, one chord per bar.
  b[0].voices[0] = [
    ring(E8, pos('E3', [6, 0]), { chord: { name: 'Em' }, dynamics: 'mp' }),
    ring(E8, pos('B3', [5, 2])),
    ring(E8, pos('E4', [4, 2])),
    ring(E8, pos('G4', [3, 0])),
    ring(E8, pos('B4', [2, 0])),
    ring(E8, pos('G4', [3, 0])),
    ring(E8, pos('E4', [4, 2])),
    ring(E8, pos('B3', [5, 2])),
  ];
  b[1].voices[0] = [
    ring(E8, pos('C4', [5, 3]), { chord: { name: 'C' } }),
    ring(E8, pos('E4', [4, 2])),
    ring(E8, pos('G4', [3, 0])),
    ring(E8, pos('C5', [2, 1])),
    ring(E8, pos('E5', [1, 0])),
    ring(E8, pos('C5', [2, 1])),
    ring(E8, pos('G4', [3, 0])),
    ring(E8, pos('E4', [4, 2])),
  ];
  b[2].voices[0] = [
    ring(E8, pos('G3', [6, 3]), { chord: { name: 'G' } }),
    ring(E8, pos('B3', [5, 2])),
    ring(E8, pos('D4', [4, 0])),
    ring(E8, pos('G4', [3, 0])),
    ring(E8, pos('B4', [2, 0])),
    ring(E8, pos('G4', [3, 0])),
    ring(E8, pos('D4', [4, 0])),
    ring(E8, pos('B3', [5, 2])),
  ];
  b[3].voices[0] = [
    ring(E8, pos('B3', [5, 2]), { chord: { name: 'B7' } }),
    ring(E8, pos('D#4', [4, 1])),
    ring(E8, pos('A4', [3, 2])),
    ring(E8, pos('B4', [2, 0])),
    ring(E8, pos('F#5', [1, 2])),
    ring(E8, pos('B4', [2, 0])),
    ring(E8, pos('A4', [3, 2])),
    ring(E8, pos('D#4', [4, 1])),
  ];

  // B: melody with guitar techniques.
  b[4].voices[0] = [
    hit(Q, [pos('E5', [1, 0])], { chord: { name: 'Em' }, dynamics: 'mf' }),
    hit(E8, [pos('F#5', [1, 2], { hammerPull: true })]),
    hit(E8, [pos('G5', [1, 3])]),
    hit(Q, [pos('B5', [1, 7], { vibrato: true })]),
    hit(E8, [pos('A5', [1, 5])]),
    hit(E8, [pos('G5', [1, 3])]),
  ];
  b[5].voices[0] = [
    hit(E8, [pos('C5', [2, 1])], { chord: { name: 'C' } }),
    hit(E8, [pos('E5', [1, 0])]),
    hit(Q, [pos('G5', [1, 3], { slide: 'legato' })]),
    hit(E8, [pos('A5', [1, 5])]),
    hit(E8, [pos('G5', [1, 3])]),
    hit(Q, [pos('E5', [1, 0])], { articulations: ['staccato'] }),
  ];
  b[6].voices[0] = [
    hit(T, [pos('B4', [2, 0])], { chord: { name: 'G' } }),
    hit(T, [pos('D5', [2, 3])]),
    hit(T, [pos('G5', [1, 3])]),
    hit(Q, [pos('B5', [1, 7])], { articulations: ['accent'] }),
    hit(E8, [pos('A5', [1, 5])]),
    hit(E8, [pos('G5', [1, 3])]),
    hit(Q, [pos('F#5', [1, 2])]),
  ];
  b[7].voices[0] = [
    hit(Q, [pos('D#5', [2, 4], { bend: { points: [{ t: 0, semis: 0 }, { t: 0.5, semis: 2 }, { t: 1, semis: 2 }] } })], { chord: { name: 'B7' } }),
    hit(E8, [pos('F#5', [1, 2])]),
    hit(E8, [pos('D#5', [2, 4])]),
    hit(H, [pos('B4', [2, 0])]),
  ];
  b[7].voices[0][0].hairpin = { type: 'cresc', endBeatId: b[7].voices[0][3].id };

  // C: strummed chords, repeated, with two endings.
  b[8].voices[0] = strumBar(EM, 'Em', { dynamics: 'f' });
  b[9].voices[0] = strumBar(CMAJ, 'C');
  b[10].voices[0] = strumBar(GMAJ, 'G');
  b[11].voices[0] = [hit(H, B7(), { strum: 'down', chord: { name: 'B7' } }), hit(H, EM(), { arpeggio: 'up', chord: { name: 'Em' }, dynamics: 'p', articulations: ['fermata'] })];

  return fingerAll(s);
}
